import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGRNs, createGRN, confirmGRN } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Plus, Check } from 'lucide-react';

export default function GRNPage() {
  const [grns, setGrns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ supplier_name: '', date_received: new Date().toISOString().split('T')[0], po_ref: '', notes: '' });
  const navigate = useNavigate();

  useEffect(() => { loadGRNs(); }, []);

  const loadGRNs = async () => {
    try { const res = await getGRNs(); setGrns(res.data); }
    catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!form.supplier_name) return toast.error('Supplier name required');
    try {
      const res = await createGRN(form);
      toast.success(`GRN ${res.data.grn_ref} created`);
      setShowCreate(false);
      setForm({ supplier_name: '', date_received: new Date().toISOString().split('T')[0], po_ref: '', notes: '' });
      loadGRNs();
    } catch (err) { toast.error('Failed'); }
  };

  const handleConfirm = async (id) => {
    try {
      await confirmGRN(id);
      toast.success('GRN confirmed');
      loadGRNs();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="grn-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Goods Received Notes</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="h-9 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="create-grn-btn">
              <Plus size={14} className="mr-1" /> New GRN
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create GRN</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Supplier Name *</Label><Input value={form.supplier_name} onChange={e => setForm(p => ({...p, supplier_name: e.target.value}))} className="h-9 text-sm" data-testid="grn-supplier" /></div>
              <div><Label className="text-xs">Date Received</Label><Input type="date" value={form.date_received} onChange={e => setForm(p => ({...p, date_received: e.target.value}))} className="h-9 text-sm" /></div>
              <div><Label className="text-xs">PO Ref</Label><Input value={form.po_ref} onChange={e => setForm(p => ({...p, po_ref: e.target.value}))} className="h-9 text-sm" /></div>
              <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="h-9 text-sm" /></div>
              <Button onClick={handleCreate} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="confirm-grn">Create GRN</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {grns.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Truck size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No GRNs recorded yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {grns.map(g => (
            <Card key={g.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm">{g.grn_ref}</span>
                    <Badge variant={g.status === 'confirmed' ? 'default' : 'secondary'} className="text-xs">{g.status}</Badge>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{g.supplier_name} · {g.date_received}</div>
                  {g.po_ref && <div className="text-xs text-gray-400">PO: {g.po_ref}</div>}
                </div>
                {g.status === 'draft' && (
                  <Button onClick={() => handleConfirm(g.id)} variant="outline" size="sm" className="text-xs h-7">
                    <Check size={12} className="mr-1" /> Confirm
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
